import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import ContactsRoundedIcon from '@mui/icons-material/ContactsRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

import { WithErrorBoundary, ButtonV2, ChipV2, dsV2 } from '@bloomays-lib/ui.shared';
import {
  dateFormat,
  getLegalContactSourceI18nKey,
  convertActivityToEachDaysArray,
  getFirstValidPrice,
  businessDaysInMonth,
  getPrivateAdminDashboardMissionPath,
} from '@bloomays-lib/utils.shared';
import type { IContact, IMissionCardDetails, ContactSide } from '@bloomays-lib/types.shared';
import { Role } from '@bloomays-lib/types.shared';
import { UPDATE_MISSION, UPSERT_SOCIETY } from '@bloomays-lib/adapter.api-bloomer';
import { useTranslation } from 'react-i18next';

import { notify } from '../../helpers/toastify';
import { computeRemainingAmount } from '../../helpers/purchaseOrderDashboard';
import { getAssessmentKey } from '../../helpers/locales';
import { useAuthStore } from '../../stores';
import {
  ActionsMenu,
  MissionActionsHost,
  EditClientSocietyDrawer,
  EditBloomerSocietyDrawer,
  ChangeBloomerSocietyDrawer,
  EditBloomerDrawer,
  DocumentsDrawer,
  ContactAddDrawer,
  ContactEditDrawer,
  ManageAllContactsDrawer,
  type ContactSource,
  type ContactGroupData,
  type MissionActionKey,
} from '../organisms/missionDashboardV2/actions';
import {
  getActiveMissionPricing,
  computeMarginRate,
  buildMissionDocuments,
  computeMissionAlerts,
  computeContractAlerts,
  formatDocumentExpirationLabel,
  resolveContactGroup,
  getContactSlot,
  deriveContactAlarms,
  pickCurrentOrPreviousCra,
  hasWorkedDayDetails,
  sumWorkedDaysForYear,
  deriveClientInvoiceProgress,
  diffInCalendarDays,
  getPrincipalContract,
  type DocumentStatus,
  type DocumentKind,
  type ContactSlot,
  type ContactSlotKey,
  type ContractDocInput,
  type MissionAlert,
} from '../../helpers/missionDashboardV2';
import { useMissionDashboard } from '../../hooks/useMissionDashboard';
import {
  SerifTitle,
  SectionLabel,
  ContactsLegend,
  Card,
  GhostButton,
  KpiCard,
  FieldRow,
  StatusPill,
  TypeTag,
  BloomerRow,
  AlertsBanner,
  ContactAlarmsBanner,
  ContactGroup,
  SocietyFields,
  SwitchRow,
  CraCalendar,
  SatisfactionComments,
  Skel,
  type Verbatim,
} from '../organisms/missionDashboardV2/components';
import { PageHeader } from '../molecules/PageHeader';

const fmtDate = (value?: string | Date | null): string => (value ? dateFormat(value, 'dd/MM/yyyy') : '—');
const fmtEuro = (value?: number | null): string =>
  value == null ? '—' : `${Math.round(value).toLocaleString('fr-FR')} €`;
const fmtDays = (value?: number | null): string => (value == null ? '—' : `J+${value}`);

// Ton de la chip « type de contrat » (créa CONTRACT_TYPES) : Bloomays→violet, devis→bleu, BDC→amber, client→vert.
const CONTRACT_TYPE_TONE: Record<string, 'accent' | 'info' | 'warning' | 'success'> = {
  bloomaysContract: 'accent',
  devis: 'info',
  bdc: 'warning',
  BDC: 'warning',
  clientContract: 'success',
};

const DOC_STATUS_TONE: Record<DocumentStatus, 'success' | 'warning' | 'error' | 'neutral' | 'info'> = {
  valid: 'success',
  warn: 'warning',
  expired: 'error',
  missing: 'error',
  pending: 'info',
};
const DOC_ICON: Record<DocumentKind, React.ReactNode> = {
  contract: <DescriptionRoundedIcon sx={{ fontSize: 17 }} />,
  id: <BadgeRoundedIcon sx={{ fontSize: 17 }} />,
  kbis: <AccountBalanceRoundedIcon sx={{ fontSize: 17 }} />,
  urssaf: <VerifiedRoundedIcon sx={{ fontSize: 17 }} />,
  rcp: <ShieldRoundedIcon sx={{ fontSize: 17 }} />,
};
// Couleur d'icône documentaire adaptée au statut (comme la grille CRA).
const docStatusColors = (status: DocumentStatus): { bg: string; fg: string } => {
  if (status === 'valid') return { bg: dsV2.color.vertDim, fg: dsV2.color.vert };
  if (status === 'warn') return { bg: dsV2.color.amberDim, fg: dsV2.color.amber };
  if (status === 'pending') return { bg: dsV2.color.bleuDim, fg: dsV2.color.bleu };
  return { bg: dsV2.color.rougeDim, fg: dsV2.color.rouge };
};

const META_DOT = (
  <Box
    component="span"
    sx={{ width: 3, height: 3, borderRadius: '50%', background: dsV2.color.border, flexShrink: 0 }}
  />
);

const MissionDashboardV2 = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  if (!id) throw new Error('Missing ID params');

  const { t } = useTranslation(['missionDashboard', 'random', 'mission']);
  const auth = useAuthStore((state) => state.auth);
  const isSystem = auth?.user?.roles?.includes(Role.System) ?? false;

  const dashboard = useMissionDashboard(id);
  const [lastRefresh, setLastRefresh] = useState<Date>(() => new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null);
  const [popinToOpen, setPopinToOpen] = useState<MissionActionKey>(null);
  const [mgmtDrawer, setMgmtDrawer] = useState<
    | 'documents'
    | 'clientSociety'
    | 'bloomerSociety'
    | 'bloomerSocietyChange'
    | 'bloomerSocietyCreate'
    | 'clientBillingSociety'
    | 'bloomer'
    | null
  >(null);
  const [contactDrawer, setContactDrawer] = useState<
    | { mode: 'add'; slot: ContactSlot; societyId?: string; societyName?: string }
    | {
        mode: 'edit';
        contact: IContact;
        slot: ContactSlot;
        source: ContactSource;
        societyId?: string;
        societyName?: string;
      }
    | null
  >(null);
  const [manageAllSide, setManageAllSide] = useState<ContactSide | null>(null);

  const openAddContact = (slot: ContactSlot, societyId?: string, societyName?: string) =>
    setContactDrawer({ mode: 'add', slot, societyId, societyName });
  const openEditContact = (
    contact: IContact,
    slot: ContactSlot,
    source: ContactSource,
    societyId?: string,
    societyName?: string,
  ) => setContactDrawer({ mode: 'edit', contact, slot, source, societyId, societyName });

  const header = dashboard.header.data;
  // Estimation de prolongation (probabilité + nouvelle date de fin estimée) — affichée dans l'en-tête,
  // éditable via la popin « Estimer la prolongation ». `header.extension` est déjà chargé par MISSION_HEADER.
  const extAssessment = header?.extension?.assessment;
  const extNewEndDate = header?.extension?.newEndDate;
  const societies = dashboard.societies.data;
  const contacts = dashboard.contacts.data;
  const bloomerSociety = societies?.bloomerBillingSociety?.[0];
  const billingSociety = societies?.clientBillingSociety ?? societies?.clientSociety;
  const billingDistinct = Boolean(
    societies?.clientBillingSociety && societies.clientBillingSociety.recordId !== societies?.clientSociety?.recordId,
  );

  // Loading par section (skeleton tant que la donnée n'est pas arrivée).
  const headerLoading = dashboard.header.loading && !header;
  const pricingLoading = dashboard.pricing.loading && !dashboard.pricing.data;
  const craLoading = dashboard.cra.loading && !dashboard.cra.data;
  const societiesLoading = dashboard.societies.loading && !societies;
  const contactsLoading = dashboard.contacts.loading && !contacts;
  const poLoading = dashboard.purchaseOrders.loading && !dashboard.purchaseOrders.data;

  // Prix actif au mois en cours (méthode partagée getFirstValidPrice).
  const pricingRows = useMemo(() => dashboard.pricing.data?.pricing ?? [], [dashboard.pricing.data]);
  const currentPricing = useMemo(() => getFirstValidPrice([...pricingRows], new Date()), [pricingRows]);
  const marginRate = computeMarginRate(
    currentPricing?.purchasePricePerDayWithoutTaxes,
    currentPricing?.salePricePerDayWithoutTaxes,
  );
  // Prix actif à la borne de début (pour les popins/avenant).
  const startPricing = useMemo(
    () => getActiveMissionPricing(pricingRows, header?.beginMission),
    [pricingRows, header?.beginMission],
  );

  // Emplacements de contacts (mission prioritaire / société repli) — source unique des cartes,
  // du drawer « gérer tout » et des alarmes de non-conformité (§8).
  const contactGroups = useMemo<ContactGroupData[]>(
    () => [
      {
        slot: getContactSlot('supplier.billing'),
        label: t('contacts.groups.billing', { ns: 'missionDashboard' }),
        emptyHint: t('contacts.empty.billing', { ns: 'missionDashboard' }),
        societyContacts: bloomerSociety?.billingContacts,
        societyId: bloomerSociety?.recordId,
        societyName: bloomerSociety?.name,
      },
      {
        slot: getContactSlot('supplier.legal'),
        label: t('contacts.groups.legalSupplier', { ns: 'missionDashboard' }),
        emptyHint: t('contacts.empty.legal', { ns: 'missionDashboard' }),
        missionContacts: contacts?.bloomerLegalContacts,
        societyContacts: bloomerSociety?.legalContacts,
        societyId: bloomerSociety?.recordId,
        societyName: bloomerSociety?.name,
      },
      {
        slot: getContactSlot('client.operational'),
        label: t('contacts.groups.operational', { ns: 'missionDashboard' }),
        emptyHint: t('contacts.empty.operational', { ns: 'missionDashboard' }),
        missionContacts: contacts?.contactOperations ? [contacts.contactOperations] : [],
      },
      {
        // Niveau « société » = société de facturation si elle existe, sinon société cliente.
        slot: getContactSlot('client.billing'),
        label: t('contacts.groups.billing', { ns: 'missionDashboard' }),
        emptyHint: t('contacts.empty.billing', { ns: 'missionDashboard' }),
        missionContacts: contacts?.billingContacts,
        societyContacts: billingSociety?.billingContacts,
        societyId: billingSociety?.recordId,
        societyName: billingSociety?.name,
      },
      {
        slot: getContactSlot('client.legal'),
        label: t('contacts.groups.legalClient', { ns: 'missionDashboard' }),
        emptyHint: t('contacts.empty.legal', { ns: 'missionDashboard' }),
        missionContacts: contacts?.clientLegalContacts,
        societyContacts: societies?.clientSociety?.legalContacts,
        societyId: societies?.clientSociety?.recordId,
        societyName: societies?.clientSociety?.name,
      },
    ],
    [contacts, societies, bloomerSociety, billingSociety, t],
  );

  const contactAlarms = useMemo(() => {
    const counts: Partial<Record<ContactSlotKey, number>> = {};
    for (const group of contactGroups) {
      counts[group.slot.key] = resolveContactGroup<IContact>({
        missionContacts: group.missionContacts,
        societyContacts: group.societyContacts,
      }).contacts.length;
    }
    return deriveContactAlarms(counts);
  }, [contactGroups]);

  const fixContactAlarm = (key: ContactSlotKey) => {
    const group = contactGroups.find((item) => item.slot.key === key);
    if (group) openAddContact(group.slot, group.societyId, group.societyName);
  };

  // États contrat PDS/SST (présent / signé) — partagés entre la liste documents et les alertes.
  const contractStates = useMemo(() => {
    const contractOf = (type: 'PDS' | 'SST'): ContractDocInput | undefined => {
      const c = getPrincipalContract(dashboard.contracts.data, type);
      if (!c) return undefined;
      // Signé = signature complète (date présente, fichier signé, ou tous les signataires ont signé).
      // Présent mais non signé ⇒ « en cours » (signature initiée automatiquement, décision #2).
      const fullySigned =
        Boolean(c.signedDate) ||
        Boolean(c.signedFileUrl) ||
        ((c.nbSigner ?? 0) > 0 && (c.signedCount ?? 0) >= (c.nbSigner ?? 0));
      return { present: true, signed: fullySigned, signedDate: c.signedDate };
    };
    return { pds: contractOf('PDS'), sst: contractOf('SST') };
  }, [dashboard.contracts.data]);

  // Documents affichés : contrats PDS/SST + CI (credentialId) + KBIS/URSSAF/RCP.
  const documents = useMemo(
    () =>
      buildMissionDocuments({
        pds: contractStates.pds,
        sst: contractStates.sst,
        credentialID: header?.bloomer?.credentialID,
        society: bloomerSociety,
      }),
    [contractStates, header?.bloomer?.credentialID, bloomerSociety],
  );

  // Bandeau d'alertes (§3) : contrats manquants (rouge) / en cours de signature (bleu, rouge si la
  // mission a démarré) + documents manquants (rouge) / expirés (jaune). Tri par sévérité (rouge d'abord).
  const alerts = useMemo(() => {
    const missionStarted = header?.beginMission
      ? diffInCalendarDays(new Date(header.beginMission), new Date()) <= 0
      : false;
    const combined = [
      ...computeContractAlerts({ pds: contractStates.pds, sst: contractStates.sst, missionStarted }),
      ...computeMissionAlerts(documents),
    ];
    const rank = (a: MissionAlert) =>
      a.status === 'missing' || (a.status === 'signing' && a.urgent) ? 0 : a.status === 'expired' ? 1 : 2;
    return [...combined].sort((x, y) => rank(x) - rank(y));
  }, [contractStates, documents, header?.beginMission]);

  const missionForPopins = useMemo<IMissionCardDetails | undefined>(() => {
    if (!header) return undefined;
    const prices = pricingRows.map((p) => ({
      startDate: p.startDate ? new Date(p.startDate) : undefined,
      purchasePricePerDayWithoutTaxes: p.purchasePricePerDayWithoutTaxes,
      salePricePerDayWithoutTaxes: p.salePricePerDayWithoutTaxes,
      taxes: p.taxes,
      recordId: p.recordId,
      astreinteCoef: p.astreinteCoef,
    }));
    return {
      recordId: header.recordId,
      label: header.label,
      externalId: header.externalId,
      beginMission: header.beginMission,
      endMission: header.endMission,
      isActive: header.isActive,
      type: header.type,
      contractType: header.contractType,
      hoursPerDays: header.hoursPerDays,
      replacement: header.replacement ?? null,
      shouldBeCountAsDeal: header.shouldBeCountAsDeal ?? false,
      enableCRAUpload: header.enableCRAUpload,
      enableBusinessReferalNpsClient: header.enableBusinessReferalNpsClient ?? false,
      enableBusinessReferalNpsBloomer: header.enableBusinessReferalNpsBloomer ?? false,
      bloomerPaydateDays: header.bloomerPaydateDays,
      clientPaydateDays: header.clientPaydateDays,
      prices,
      purchasePricePerDayWithoutTaxes: startPricing?.purchasePricePerDayWithoutTaxes,
      salePricePerDayWithoutTaxes: startPricing?.salePricePerDayWithoutTaxes,
      taxes: startPricing?.taxes,
      bloomerBillingSociety: societies?.bloomerBillingSociety,
      clientSociety: societies?.clientSociety,
      clientBillingSociety: societies?.clientBillingSociety,
      bloomer: header.bloomer,
      CSM: header.CSM?.fullname,
    } as unknown as IMissionCardDetails;
  }, [header, pricingRows, startPricing, societies]);

  const legalClientContact = contacts?.resolvedClientPdsContact?.contact;
  const legalBloomerContact = contacts?.resolvedSupplierSstContact?.contact;

  const craActivities = dashboard.cra.data ?? [];
  const currentYear = new Date().getFullYear();
  const daysWorkedYear = sumWorkedDaysForYear(craActivities, currentYear);
  const estimatedCaYear =
    currentPricing?.salePricePerDayWithoutTaxes != null
      ? daysWorkedYear * currentPricing.salePricePerDayWithoutTaxes
      : null;
  const latestClientScore = craActivities.find((a) => a.clientScore != null)?.clientScore;
  const latestBloomerScore = craActivities.find((a) => a.bloomerScore != null)?.bloomerScore;
  const recentBilling = [...craActivities]
    .sort((a, b) => new Date(b.activityMonth ?? 0).getTime() - new Date(a.activityMonth ?? 0).getTime())
    .slice(0, 3);

  // CRA à afficher : mois en cours, sinon mois précédent (§6). On privilégie un CRA réellement
  // détaillé (jours saisis) pour que la grille montre les jours travaillés en vert ; à défaut, on
  // retombe sur l'ensemble des CRA (mois en cours même vide).
  const craWithWorkedDetails = useMemo(
    () => craActivities.filter((a) => hasWorkedDayDetails(a.nbDaysDetails)),
    [craActivities],
  );
  const craForDisplay = pickCurrentOrPreviousCra(
    craWithWorkedDetails.length > 0 ? craWithWorkedDetails : craActivities,
  );
  const craDays = useMemo(() => {
    if (!craForDisplay?.activityMonth || !craForDisplay.nbDaysDetails) return [];
    try {
      return convertActivityToEachDaysArray(craForDisplay.activityMonth, craForDisplay.nbDaysDetails);
    } catch {
      return [];
    }
  }, [craForDisplay?.activityMonth, craForDisplay?.nbDaysDetails]);
  const craMonthWorked = craForDisplay
    ? (craForDisplay.nbDaysWorked ?? 0) +
      (craForDisplay.nbDaysMarkupWorked ?? 0) +
      (craForDisplay.nbDaysMarkup2Worked ?? 0)
    : 0;
  const craBusinessDays = craForDisplay?.activityMonth ? businessDaysInMonth(new Date(craForDisplay.activityMonth)) : 0;
  const craMonthLabel = craForDisplay?.activityMonth ? dateFormat(craForDisplay.activityMonth, 'MMMM yyyy') : '';

  const progress = useMemo(() => {
    if (!header?.beginMission || !header?.endMission) return 0;
    const begin = new Date(header.beginMission);
    const end = new Date(header.endMission);
    const total = diffInCalendarDays(end, begin);
    if (total <= 0) return header.isActive ? 0 : 100;
    return Math.min(100, Math.max(0, Math.round((diffInCalendarDays(new Date(), begin) / total) * 100)));
  }, [header?.beginMission, header?.endMission, header?.isActive]);

  // Toggles BDC requis (mission + client de facturation).
  const [poMission, setPoMission] = useState(false);
  const [poClient, setPoClient] = useState(false);
  useEffect(
    () => setPoMission(Boolean(dashboard.purchaseOrders.data?.purchaseOrderRequired)),
    [dashboard.purchaseOrders.data?.purchaseOrderRequired],
  );
  useEffect(() => setPoClient(Boolean(billingSociety?.orderNumberRequired)), [billingSociety?.orderNumberRequired]);
  const [updateMissionMutation] = useMutation(UPDATE_MISSION);
  const [upsertSocietyMutation] = useMutation(UPSERT_SOCIETY);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dashboard.refetchAll();
      setLastRefresh(new Date());
    } catch (error) {
      notify('error', t('page.refreshError', { ns: 'missionDashboard' }), error as Error, undefined);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleMissionPo = async (checked: boolean) => {
    setPoMission(checked);
    try {
      await updateMissionMutation({ variables: { input: { recordId: id, purchaseOrderRequired: checked } } });
      notify('success', t('purchaseOrders.missionUpdated', { ns: 'missionDashboard' }));
      void dashboard.refetchAll();
    } catch (error) {
      setPoMission(!checked);
      notify('error', t('purchaseOrders.missionUpdateError', { ns: 'missionDashboard' }), error as Error, undefined);
    }
  };
  const toggleClientPo = async (checked: boolean) => {
    if (!billingSociety?.recordId) return;
    setPoClient(checked);
    try {
      await upsertSocietyMutation({
        variables: { input: { recordId: billingSociety.recordId, orderNumberRequired: checked } },
      });
      notify('success', t('purchaseOrders.clientUpdated', { ns: 'missionDashboard' }));
      void dashboard.refetchAll();
    } catch (error) {
      setPoClient(!checked);
      notify('error', t('purchaseOrders.clientUpdateError', { ns: 'missionDashboard' }), error as Error, undefined);
    }
  };

  const openPopin = (popin: MissionActionKey) => {
    setActionsAnchor(null);
    setPopinToOpen(popin);
  };
  const handlePopinReset = () => {
    setPopinToOpen(null);
    void dashboard.refetchAll();
  };

  const beginInFuture = header ? diffInCalendarDays(new Date(header.beginMission), new Date()) > 0 : false;
  const daysSinceEnd = header ? diffInCalendarDays(new Date(), new Date(header.endMission)) : 0;
  const withinGracePeriod = Boolean(header && !header.isActive && daysSinceEnd > 0 && daysSinceEnd <= 5);
  const actionItems: { label: string; popin: MissionActionKey; show: boolean; danger?: boolean }[] = [
    {
      label: t('actions.shortenMission', { ns: 'missionDashboard' }),
      popin: 'shorten',
      show: Boolean(header?.isActive),
    },
    {
      label: t('actions.createAmendment', { ns: 'missionDashboard' }),
      popin: 'amendment',
      show: Boolean(header?.isActive) || withinGracePeriod || isSystem,
    },
    {
      label: t('actions.modifyMission', { ns: 'missionDashboard' }),
      popin: 'modify',
      show: Boolean(header?.isActive) || beginInFuture,
    },
    {
      label: t('actions.estimateExtension', { ns: 'missionDashboard' }),
      popin: 'assessment',
      show: Boolean(header?.isActive),
    },
    {
      label: t('actions.priceHistory', { ns: 'missionDashboard' }),
      popin: 'prices',
      show: (missionForPopins?.prices?.length ?? 0) > 0,
    },
    { label: t('actions.deleteMission', { ns: 'missionDashboard' }), popin: 'delete', show: isSystem, danger: true },
  ];

  const bloomerName =
    header?.bloomer?.fullname ||
    `${header?.bloomer?.firstname ?? ''} ${header?.bloomer?.lastname ?? ''}`.trim() ||
    t('header.bloomerFallback', { ns: 'missionDashboard' });
  const jobOwnerName = header?.jobOwner
    ? `${header.jobOwner.firstname ?? ''} ${header.jobOwner.lastname ?? ''}`.trim()
    : t('header.jobOwnerUnassigned', { ns: 'missionDashboard' });
  const clientName = societies?.clientSociety?.name || '—';

  const verbatims: Verbatim[] = craActivities.flatMap((a) => {
    const items: Verbatim[] = [];
    if (a.clientComment)
      items.push({
        from: 'client',
        text: a.clientComment,
        author: clientName,
        date: a.clientScoreDate ? fmtDate(a.clientScoreDate) : undefined,
      });
    if (a.bloomerComment)
      items.push({
        from: 'bloomer',
        text: a.bloomerComment,
        author: bloomerName,
        date: a.bloomerScoreDate ? fmtDate(a.bloomerScoreDate) : undefined,
      });
    return items;
  });

  const metaText = {
    fontSize: '12.5px',
    color: dsV2.color.t2,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  };
  const cardIconSx = { fontSize: 15 };
  // Sous-ligne « {société} · {statut} » sous le titre de carte contacts (créa cp-company).
  const cardCompanySx = {
    textTransform: 'none' as const,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: 0,
    color: dsV2.color.t3,
    marginTop: '1px',
  };
  const purchaseOrders = dashboard.purchaseOrders.data?.purchaseOrders ?? [];

  return (
    <Box
      sx={{
        background: dsV2.color.bg,
        minHeight: '100%',
        fontFamily: dsV2.font.sans,
        color: dsV2.color.t1,
        textAlign: 'left',
      }}
    >
      <Box sx={{ maxWidth: 1120, margin: '0 auto', padding: '24px 20px 90px' }}>
        {/* En-tête de page — contrôle commun PageHeader */}
        <PageHeader
          title={t('page.title', { ns: 'missionDashboard' })}
          subtitle={t('page.subtitle', { ns: 'missionDashboard' })}
          onRefresh={handleRefresh}
          lastUpdatedAt={lastRefresh}
          refreshing={refreshing}
          refreshLabel={t('page.refresh', { ns: 'missionDashboard' })}
        />

        {/* Bandeau d'alertes — masqué tant que les sociétés chargent (évite un flash « docs manquants »). */}
        {!societiesLoading && <AlertsBanner alerts={alerts} onRegularize={() => setMgmtDrawer('documents')} />}

        {/* En-tête mission */}
        {headerLoading ? (
          <Box sx={{ marginBottom: '14px' }}>
            <Skel height={190} />
          </Box>
        ) : (
          <Box
            sx={{
              background: dsV2.color.blanc,
              border: `1px solid ${dsV2.color.border}`,
              borderRadius: dsV2.radius.lg,
              boxShadow: dsV2.shadow.sm,
              padding: '22px 26px',
              marginBottom: '14px',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
              gap: '20px',
              alignItems: 'start',
            }}
          >
            <Box>
              <Stack direction="row" spacing="8px" alignItems="center" sx={{ marginBottom: '11px', flexWrap: 'wrap' }}>
                <StatusPill active={Boolean(header?.isActive)} />
                {header?.type ? <TypeTag>{header.type}</TypeTag> : null}
                {header?.replacement ? (
                  <ChipV2 label={t('header.replacement', { ns: 'missionDashboard' })} tone="accent" size="small" />
                ) : null}
              </Stack>
              <SerifTitle
                sx={{
                  display: 'block',
                  fontSize: 'clamp(22px,3vw,30px)',
                  letterSpacing: '-0.015em',
                  lineHeight: 1.12,
                  marginBottom: '11px',
                }}
              >
                {header?.label || t('header.missionFallback', { ns: 'missionDashboard' })}
              </SerifTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <Box sx={metaText}>
                  <PersonRoundedIcon sx={{ fontSize: 14, color: dsV2.color.t3 }} />
                  <Box component="strong" sx={{ color: dsV2.color.noir, fontWeight: 600 }}>
                    {bloomerName}
                  </Box>
                </Box>
                {META_DOT}
                <Box sx={metaText}>
                  <ApartmentRoundedIcon sx={{ fontSize: 14, color: dsV2.color.t3 }} />
                  <Box component="strong" sx={{ color: dsV2.color.noir, fontWeight: 600 }}>
                    {clientName}
                  </Box>
                </Box>
                {META_DOT}
                <Box
                  component="span"
                  sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '12.5px', color: dsV2.color.t2 }}
                >
                  {fmtDate(header?.beginMission)} → {fmtDate(header?.endMission)}
                </Box>
                {META_DOT}
                <Box component="span" sx={{ color: dsV2.color.t4, fontSize: '11.5px' }}>
                  {t('header.jobOwner', { ns: 'missionDashboard', name: jobOwnerName })}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', maxWidth: 560 }}>
                <Box
                  component="span"
                  sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 11, color: dsV2.color.t4, whiteSpace: 'nowrap' }}
                >
                  {fmtDate(header?.beginMission).slice(0, 5)}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    height: 5,
                    background: dsV2.color.surface2,
                    borderRadius: '100px',
                    overflow: 'hidden',
                    border: `1px solid ${dsV2.color.borderLight}`,
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${progress}%`,
                      background: dsV2.color.vert,
                      borderRadius: '100px',
                      transition: 'width 1s cubic-bezier(.4,0,.2,1)',
                    }}
                  />
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 12,
                    fontWeight: 500,
                    color: dsV2.color.vert,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {progress}%
                </Box>
                <Box
                  component="span"
                  sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 11, color: dsV2.color.t4, whiteSpace: 'nowrap' }}
                >
                  {fmtDate(header?.endMission).slice(0, 5)}
                </Box>
              </Box>
              {/* Estimation de prolongation (créa) : chip cliquable → popin d'estimation ; lien discret si non estimée. */}
              {extAssessment != null ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => openPopin('assessment')}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    alignSelf: 'flex-start',
                    marginTop: '12px',
                    padding: '5px 10px',
                    borderRadius: dsV2.radius.sm,
                    border: `1px solid ${dsV2.color.bleuBrd}`,
                    background: dsV2.color.bleuDim,
                    color: dsV2.color.bleuDark,
                    fontSize: '11.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <CalendarMonthRoundedIcon sx={{ fontSize: 13 }} />
                  {t('header.extensionLabel', { ns: 'missionDashboard' })} :{' '}
                  {t(getAssessmentKey(extAssessment ?? 0), { ns: 'extendShortenMission' })}
                  {extNewEndDate
                    ? ` · ${t('header.extensionNewEnd', { ns: 'missionDashboard', date: fmtDate(extNewEndDate) })}`
                    : ''}
                </Box>
              ) : (
                <Box
                  component="button"
                  type="button"
                  onClick={() => openPopin('assessment')}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    alignSelf: 'flex-start',
                    marginTop: '12px',
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    color: dsV2.color.t3,
                    fontSize: '11.5px',
                    cursor: 'pointer',
                    '&:hover': { color: dsV2.color.bleu, textDecoration: 'underline' },
                  }}
                >
                  <CalendarMonthRoundedIcon sx={{ fontSize: 13 }} />{' '}
                  {t('actions.estimateExtension', { ns: 'missionDashboard' })}
                </Box>
              )}
            </Box>
            <Stack
              direction={{ xs: 'row', md: 'column' }}
              spacing="8px"
              alignItems={{ xs: 'center', md: 'flex-end' }}
              sx={{ flexWrap: 'wrap' }}
            >
              <ButtonV2
                startIcon={<BoltRoundedIcon sx={{ fontSize: 15 }} />}
                onClick={(e) => setActionsAnchor(e.currentTarget)}
              >
                {t('actions.actions', { ns: 'missionDashboard' })}
              </ButtonV2>
              <ButtonV2
                variant="secondary"
                startIcon={<EditRoundedIcon sx={{ fontSize: 14 }} />}
                onClick={() => openPopin('modify')}
              >
                {t('actions.modify', { ns: 'missionDashboard' })}
              </ButtonV2>
              <ActionsMenu
                anchorEl={actionsAnchor}
                onClose={() => setActionsAnchor(null)}
                items={actionItems
                  .filter((item) => item.show)
                  .map((item) => ({
                    key: item.popin ?? '',
                    label: item.label,
                    danger: item.danger,
                    onClick: () => openPopin(item.popin),
                  }))}
                navItems={[
                  {
                    key: 'cra',
                    label: t('actions.viewCra', { ns: 'missionDashboard' }),
                    onClick: () => navigate(`${getPrivateAdminDashboardMissionPath(id)}/activities`),
                  },
                  {
                    key: 'contracts',
                    label: t('actions.manageContracts', { ns: 'missionDashboard' }),
                    onClick: () => navigate(`${getPrivateAdminDashboardMissionPath(id)}/contracts`),
                  },
                ]}
              />
            </Stack>
          </Box>
        )}

        {/* Hôte des actions mission V2 (popins + drawer d'édition) */}
        {missionForPopins ? (
          <MissionActionsHost
            openKey={popinToOpen}
            onClose={handlePopinReset}
            onOpen={setPopinToOpen}
            mission={missionForPopins}
            jobOwnerName={jobOwnerName}
            legalClientContact={legalClientContact}
            legalBloomerContact={legalBloomerContact}
            legalClientSourceLabel={t(getLegalContactSourceI18nKey(contacts?.resolvedClientPdsContact?.source), {
              ns: 'mission',
            })}
            legalBloomerSourceLabel={t(getLegalContactSourceI18nKey(contacts?.resolvedSupplierSstContact?.source), {
              ns: 'mission',
            })}
            legalContactUnavailableText={t('notAvailable', { ns: 'mission' })}
          />
        ) : null}

        {/* Sidebar de gestion des documents (attestations société Bloomer + carte d'identité) */}
        {mgmtDrawer === 'documents' ? (
          <DocumentsDrawer
            open
            onClose={() => {
              setMgmtDrawer(null);
              void dashboard.refetchAll();
            }}
            onSaved={() => void dashboard.refetchAll()}
            missionRecordId={id}
            bloomerSociety={bloomerSociety}
            bloomer={header?.bloomer ?? undefined}
            contracts={dashboard.contracts.data}
          />
        ) : null}

        {/* Sidebar de gestion de la société cliente */}
        {mgmtDrawer === 'clientSociety' && societies?.clientSociety ? (
          <EditClientSocietyDrawer
            open
            onClose={() => {
              setMgmtDrawer(null);
              void dashboard.refetchAll();
            }}
            clientSociety={societies.clientSociety}
            clientBillingSociety={societies.clientBillingSociety}
            missionRecordId={id}
            contractType={header?.contractType}
          />
        ) : null}

        {/* Sidebar de gestion de la société de facturation cliente */}
        {mgmtDrawer === 'clientBillingSociety' && societies?.clientBillingSociety ? (
          <EditClientSocietyDrawer
            open
            variant="billing"
            onClose={() => {
              setMgmtDrawer(null);
              void dashboard.refetchAll();
            }}
            clientSociety={societies.clientBillingSociety}
            missionRecordId={id}
          />
        ) : null}

        {/* Édition en place de la société du Bloomer (verrouillée pour le portage hors System) ; en mode
            'bloomerSocietyCreate' le formulaire s'ouvre vide → le upsert crée la société ET la rattache. */}
        {mgmtDrawer === 'bloomerSociety' || mgmtDrawer === 'bloomerSocietyCreate' ? (
          <EditBloomerSocietyDrawer
            open
            onClose={() => {
              setMgmtDrawer(null);
              void dashboard.refetchAll();
            }}
            society={mgmtDrawer === 'bloomerSocietyCreate' ? undefined : bloomerSociety}
            isSystem={isSystem}
            previousSocietyIdToUnlink={mgmtDrawer === 'bloomerSocietyCreate' ? bloomerSociety?.recordId : undefined}
            missionRecordId={id}
          />
        ) : null}

        {/* Changer de société du Bloomer : rechercher une société existante (rattachement sans écrasement)
            ou créer une nouvelle — évite d'écraser une société de portage partagée. */}
        {mgmtDrawer === 'bloomerSocietyChange' ? (
          <ChangeBloomerSocietyDrawer
            open
            onClose={() => {
              setMgmtDrawer(null);
              void dashboard.refetchAll();
            }}
            onSaved={() => void dashboard.refetchAll()}
            onCreateNew={() => setMgmtDrawer('bloomerSocietyCreate')}
            missionRecordId={id}
            currentSocietyId={bloomerSociety?.recordId}
          />
        ) : null}

        {/* Sidebar d'édition de la personne Bloomer (profil) */}
        {mgmtDrawer === 'bloomer' && header?.bloomer ? (
          <EditBloomerDrawer
            open
            onClose={() => {
              setMgmtDrawer(null);
              void dashboard.refetchAll();
            }}
            bloomer={header.bloomer}
          />
        ) : null}

        {/* Sidebar d'ajout de contact (recherche annuaire + liaison) */}
        {contactDrawer?.mode === 'add' ? (
          <ContactAddDrawer
            open
            onClose={() => setContactDrawer(null)}
            slot={contactDrawer.slot}
            missionId={id}
            societyId={contactDrawer.societyId}
            societyName={contactDrawer.societyName}
            onDone={() => dashboard.refetchAll()}
          />
        ) : null}

        {/* Sidebar d'édition / déliaison de contact */}
        {contactDrawer?.mode === 'edit' ? (
          <ContactEditDrawer
            open
            onClose={() => setContactDrawer(null)}
            contact={contactDrawer.contact}
            slot={contactDrawer.slot}
            source={contactDrawer.source}
            missionId={id}
            societyId={contactDrawer.societyId}
            societyName={contactDrawer.societyName}
            onDone={() => dashboard.refetchAll()}
            onOpenSociety={() => {
              const target = contactDrawer.slot.side === 'supplier' ? 'bloomerSociety' : 'clientSociety';
              setContactDrawer(null);
              setMgmtDrawer(target);
            }}
          />
        ) : null}

        {/* Sidebar de gestion globale des contacts (promote additif / retrait / édition) */}
        {manageAllSide ? (
          <ManageAllContactsDrawer
            open
            side={manageAllSide}
            onClose={() => setManageAllSide(null)}
            missionId={id}
            groups={contactGroups}
            bloomer={manageAllSide === 'supplier' ? header?.bloomer ?? undefined : undefined}
            bloomerSubtitle={t('header.jobOwner', { ns: 'missionDashboard', name: jobOwnerName })}
            onDone={() => dashboard.refetchAll()}
            onEditContact={(contact, slot, source, societyId, societyName) => {
              setManageAllSide(null);
              openEditContact(contact, slot, source, societyId, societyName);
            }}
            onEditBloomer={() => {
              setManageAllSide(null);
              setMgmtDrawer('bloomer');
            }}
            onOpenSociety={(side) => {
              setManageAllSide(null);
              setMgmtDrawer(side === 'supplier' ? 'bloomerSociety' : 'clientSociety');
            }}
          />
        ) : null}

        {/* KPI */}
        {pricingLoading || craLoading ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(135px,1fr))',
              gap: '10px',
              marginBottom: '8px',
            }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <Skel key={i} height={92} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(135px,1fr))',
              gap: '10px',
              marginBottom: '8px',
            }}
          >
            <KpiCard
              label={t('kpi.purchaseTjm', { ns: 'missionDashboard' })}
              value={fmtEuro(currentPricing?.purchasePricePerDayWithoutTaxes)}
              sub={t('kpi.perDay', { ns: 'missionDashboard' })}
              accent="vert"
            />
            <KpiCard
              label={t('kpi.saleTjm', { ns: 'missionDashboard' })}
              value={fmtEuro(currentPricing?.salePricePerDayWithoutTaxes)}
              sub={marginRate != null ? t('kpi.margin', { ns: 'missionDashboard', rate: marginRate }) : undefined}
              accent="solaire"
            />
            <KpiCard
              label={t('kpi.billedDays', { ns: 'missionDashboard' })}
              value={daysWorkedYear.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
              sub={t('kpi.craCumulative', { ns: 'missionDashboard', year: currentYear })}
              accent="bleu"
            />
            <KpiCard
              label={t('kpi.generatedRevenue', { ns: 'missionDashboard' })}
              value={fmtEuro(estimatedCaYear)}
              sub={t('kpi.estimated', { ns: 'missionDashboard', year: currentYear })}
              accent="noir"
            />
            <KpiCard
              label={t('kpi.bloomerDelay', { ns: 'missionDashboard' })}
              value={fmtDays(header?.bloomerPaydateDays)}
              sub={t('kpi.payment', { ns: 'missionDashboard' })}
            />
            <KpiCard
              label={t('kpi.clientDelay', { ns: 'missionDashboard' })}
              value={fmtDays(header?.clientPaydateDays)}
              sub={t('kpi.payment', { ns: 'missionDashboard' })}
            />
          </Box>
        )}

        {/* Activité & facturation */}
        <SectionLabel>{t('sections.billing', { ns: 'missionDashboard' })}</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px' }}>
          {craLoading ? (
            <Skel height={230} />
          ) : (
            <Card
              icon={<CalendarMonthRoundedIcon sx={cardIconSx} />}
              iconTone="bleu"
              title={
                craMonthLabel
                  ? t('cards.activityWithMonth', { ns: 'missionDashboard', month: craMonthLabel })
                  : t('cards.activityFallback', { ns: 'missionDashboard' })
              }
              action={
                <GhostButton onClick={() => navigate(`${getPrivateAdminDashboardMissionPath(id)}/activities`)}>
                  {t('actions.viewAll', { ns: 'missionDashboard' })}
                </GhostButton>
              }
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginBottom: '13px' }}>
                <Box>
                  <Box sx={{ fontSize: 20, fontWeight: 700, color: dsV2.color.noir }}>
                    {craMonthWorked.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
                    <Box component="span" sx={{ fontSize: 13, color: dsV2.color.t4, fontWeight: 400 }}>
                      {craBusinessDays > 0
                        ? t('activity.daysTotal', { ns: 'missionDashboard', count: craBusinessDays })
                        : t('activity.daysUnit', { ns: 'missionDashboard' })}
                    </Box>
                  </Box>
                  <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>
                    {t('activity.craCount', {
                      ns: 'missionDashboard',
                      count: craActivities.length,
                      year: currentYear,
                      days: daysWorkedYear,
                    })}
                  </Box>
                </Box>
                <ChipV2
                  label={t('activity.baseHours', { ns: 'missionDashboard', hours: header?.hoursPerDays ?? '—' })}
                  tone="neutral"
                />
              </Stack>
              {craDays.length > 0 ? (
                <CraCalendar days={craDays} />
              ) : (
                <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic' }}>
                  {t('activity.dailyDetailUnavailable', { ns: 'missionDashboard' })}
                </Box>
              )}
            </Card>
          )}
          {craLoading ? (
            <Skel height={230} />
          ) : (
            <Card
              icon={<PaymentsRoundedIcon sx={cardIconSx} />}
              iconTone="vert"
              title={t('cards.billingTracking', { ns: 'missionDashboard' })}
              action={
                <GhostButton onClick={() => navigate(`${getPrivateAdminDashboardMissionPath(id)}/activities`)}>
                  {t('actions.manage', { ns: 'missionDashboard' })}
                </GhostButton>
              }
            >
              <Box
                sx={{
                  fontSize: '10.5px',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: dsV2.color.t4,
                  marginBottom: '10px',
                }}
              >
                {t('billing.lastThreeMonths', { ns: 'missionDashboard' })}
              </Box>
              {recentBilling.length > 0 ? (
                recentBilling.map((b) => {
                  const progressInvoice = deriveClientInvoiceProgress(b.clientInvoiceStatus);
                  const barColor =
                    progressInvoice.bar === 'green'
                      ? dsV2.color.vert
                      : progressInvoice.bar === 'amber'
                      ? dsV2.color.amber
                      : progressInvoice.bar === 'red'
                      ? dsV2.color.rouge
                      : dsV2.color.t4;
                  return (
                    <Box
                      key={b.activityId ?? b.activityMonth}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 0',
                        borderBottom: `1px solid ${dsV2.color.borderLight}`,
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: 12,
                          color: dsV2.color.t2,
                          fontVariantNumeric: 'tabular-nums',
                          width: 64,
                          flexShrink: 0,
                        }}
                      >
                        {b.activityMonth ? dateFormat(b.activityMonth, 'MM/yyyy') : '—'}
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          height: 5,
                          background: dsV2.color.surface2,
                          borderRadius: '100px',
                          overflow: 'hidden',
                          border: `1px solid ${dsV2.color.borderLight}`,
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${progressInvoice.percent}%`,
                            background: barColor,
                            borderRadius: '100px',
                          }}
                        />
                      </Box>
                      <Box sx={{ width: 96, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                        <ChipV2 label={progressInvoice.label} tone={progressInvoice.chip} size="small" />
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic' }}>
                  {t('billing.noRecentBilling', { ns: 'missionDashboard' })}
                </Box>
              )}
            </Card>
          )}
        </Box>

        {/* Satisfaction & documents */}
        <SectionLabel>{t('sections.satisfactionDocuments', { ns: 'missionDashboard' })}</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px' }}>
          {craLoading ? (
            <Skel height={200} />
          ) : (
            <Card
              icon={<StarRoundedIcon sx={cardIconSx} />}
              iconTone="vert"
              title={t('cards.satisfaction', { ns: 'missionDashboard' })}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' }}>
                {[
                  {
                    label: t('satisfaction.client', { ns: 'missionDashboard' }),
                    value: latestClientScore,
                    color: dsV2.color.violet,
                  },
                  {
                    label: t('satisfaction.freelance', { ns: 'missionDashboard' }),
                    value: latestBloomerScore,
                    color: dsV2.color.bleu,
                  },
                  {
                    label: t('satisfaction.global', { ns: 'missionDashboard' }),
                    value:
                      latestClientScore != null && latestBloomerScore != null
                        ? Math.round(((latestClientScore + latestBloomerScore) / 2) * 10) / 10
                        : latestClientScore ?? latestBloomerScore,
                    color: dsV2.color.vert,
                  },
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      background: dsV2.color.surface2,
                      border: `1px solid ${dsV2.color.borderLight}`,
                      borderRadius: dsV2.radius.sm,
                      padding: '13px 8px',
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 23,
                        fontWeight: 700,
                        lineHeight: 1,
                        marginBottom: '3px',
                        color: s.value != null ? s.color : dsV2.color.t4,
                      }}
                    >
                      {s.value != null ? s.value : '—'}
                    </Box>
                    <Box
                      sx={{
                        fontSize: '9.5px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.09em',
                        color: dsV2.color.t4,
                      }}
                    >
                      {s.label}
                    </Box>
                  </Box>
                ))}
              </Box>
              <SatisfactionComments comments={verbatims} />
            </Card>
          )}
          {societiesLoading ? (
            <Skel height={200} />
          ) : (
            <Card
              icon={<FolderOpenRoundedIcon sx={cardIconSx} />}
              iconTone="neutral"
              title={t('cards.documents', { ns: 'missionDashboard' })}
              id="section-documents"
              action={
                <GhostButton onClick={() => setMgmtDrawer('documents')}>
                  {t('actions.manage', { ns: 'missionDashboard' })}
                </GhostButton>
              }
              bodySx={{ display: 'flex', flexDirection: 'column' }}
            >
              {documents.map((doc) => {
                const tone = docStatusColors(doc.status);
                return (
                  <Box
                    key={doc.id ?? doc.kind}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      padding: '8px 0',
                      borderBottom: `1px solid ${dsV2.color.borderLight}`,
                      '&:last-of-type': { borderBottom: 'none' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '7px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          background: tone.bg,
                          color: tone.fg,
                        }}
                      >
                        {DOC_ICON[doc.kind]}
                      </Box>
                      <Box>
                        <Box sx={{ fontSize: '12.5px', fontWeight: 500, color: dsV2.color.noir }}>
                          {doc.name}
                          {doc.optional ? t('documents.optionalSuffix', { ns: 'missionDashboard' }) : ''}
                        </Box>
                        <Box
                          sx={{
                            fontSize: 11,
                            color:
                              doc.status === 'expired' || doc.status === 'missing' ? dsV2.color.rouge : dsV2.color.t4,
                          }}
                        >
                          {formatDocumentExpirationLabel(doc)}
                        </Box>
                      </Box>
                    </Box>
                    <ChipV2
                      label={t(`docStatusLabel.${doc.status}`, { ns: 'missionDashboard' })}
                      tone={DOC_STATUS_TONE[doc.status]}
                      size="small"
                    />
                  </Box>
                );
              })}
            </Card>
          )}
        </Box>

        {/* Contacts & interlocuteurs */}
        <SectionLabel>{t('sections.contacts', { ns: 'missionDashboard' })}</SectionLabel>
        <ContactsLegend />
        {!contactsLoading && !societiesLoading ? (
          <ContactAlarmsBanner alarms={contactAlarms} onFix={fixContactAlarm} />
        ) : null}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px' }}>
          {contactsLoading || societiesLoading ? (
            <Skel height={220} />
          ) : (
            <Card
              icon={<ContactsRoundedIcon sx={cardIconSx} />}
              iconTone="violet"
              title={
                <Box>
                  {t('cards.supplier', { ns: 'missionDashboard' })}
                  {bloomerSociety ? (
                    <Box sx={cardCompanySx}>
                      {bloomerSociety.name}
                      {bloomerSociety.status ? ` · ${bloomerSociety.status}` : ''}
                    </Box>
                  ) : null}
                </Box>
              }
              bodySx={{ padding: 0 }}
              action={
                <GhostButton onClick={() => setManageAllSide('supplier')}>
                  <TuneRoundedIcon sx={{ fontSize: 14 }} /> {t('actions.manageAll', { ns: 'missionDashboard' })}
                </GhostButton>
              }
            >
              {header?.bloomer ? (
                <BloomerRow
                  name={bloomerName}
                  subtitle={t('header.jobOwner', { ns: 'missionDashboard', name: jobOwnerName })}
                  email={header.bloomer.email}
                  phoneNumber={header.bloomer.phoneNumber}
                  onEdit={() => setMgmtDrawer('bloomer')}
                />
              ) : null}
              {contactGroups
                .filter((group) => group.slot.side === 'supplier')
                .map((group) => (
                  <ContactGroup
                    key={group.slot.key}
                    label={group.label}
                    slot={group.slot}
                    missionContacts={group.missionContacts}
                    societyContacts={group.societyContacts}
                    emptyHint={group.emptyHint}
                    societyId={group.societyId}
                    societyName={group.societyName}
                    onAdd={openAddContact}
                    onEdit={openEditContact}
                    onManageAll={(side) => setManageAllSide(side)}
                  />
                ))}
            </Card>
          )}
          {contactsLoading || societiesLoading ? (
            <Skel height={220} />
          ) : (
            <Card
              icon={<ContactsRoundedIcon sx={cardIconSx} />}
              iconTone="bleu"
              title={
                <Box>
                  {t('cards.client', { ns: 'missionDashboard' })}
                  {societies?.clientSociety ? (
                    <Box sx={cardCompanySx}>
                      {societies.clientSociety.name}
                      {societies.clientSociety.status ? ` · ${societies.clientSociety.status}` : ''}
                    </Box>
                  ) : null}
                </Box>
              }
              bodySx={{ padding: 0 }}
              action={
                <GhostButton onClick={() => setManageAllSide('client')}>
                  <TuneRoundedIcon sx={{ fontSize: 14 }} /> {t('actions.manageAll', { ns: 'missionDashboard' })}
                </GhostButton>
              }
            >
              {contactGroups
                .filter((group) => group.slot.side === 'client')
                .map((group) => (
                  <ContactGroup
                    key={group.slot.key}
                    label={group.label}
                    slot={group.slot}
                    missionContacts={group.missionContacts}
                    societyContacts={group.societyContacts}
                    emptyHint={group.emptyHint}
                    societyId={group.societyId}
                    societyName={group.societyName}
                    onAdd={openAddContact}
                    onEdit={openEditContact}
                    onManageAll={(side) => setManageAllSide(side)}
                  />
                ))}
            </Card>
          )}
        </Box>

        {/* Sociétés */}
        <SectionLabel>{t('sections.societies', { ns: 'missionDashboard' })}</SectionLabel>
        {societiesLoading ? (
          <Box sx={{ marginTop: '12px' }}>
            <Skel height={170} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '12px',
              marginTop: '12px',
            }}
          >
            <Card
              icon={<BusinessRoundedIcon sx={cardIconSx} />}
              iconTone="beige"
              title={t('cards.bloomerSociety', { ns: 'missionDashboard' })}
              action={
                bloomerSociety ? (
                  <Box sx={{ display: 'inline-flex', gap: '6px' }}>
                    {!(bloomerSociety.portage && !isSystem) ? (
                      <GhostButton onClick={() => setMgmtDrawer('bloomerSociety')}>
                        {t('actions.modify', { ns: 'missionDashboard' })}
                      </GhostButton>
                    ) : null}
                    <GhostButton onClick={() => setMgmtDrawer('bloomerSocietyChange')}>
                      {t('actions.changeSociety', { ns: 'missionDashboard' })}
                    </GhostButton>
                  </Box>
                ) : (
                  <GhostButton onClick={() => setMgmtDrawer('bloomerSocietyChange')}>
                    {t('actions.attach', { ns: 'missionDashboard' })}
                  </GhostButton>
                )
              }
            >
              <SocietyFields society={bloomerSociety} kind="bloomer" />
            </Card>
            <Card
              icon={<BusinessRoundedIcon sx={cardIconSx} />}
              iconTone="bleu"
              title={t('cards.clientSociety', { ns: 'missionDashboard' })}
              action={
                societies?.clientSociety ? (
                  <GhostButton onClick={() => setMgmtDrawer('clientSociety')}>
                    {t('actions.modify', { ns: 'missionDashboard' })}
                  </GhostButton>
                ) : undefined
              }
            >
              <SocietyFields society={societies?.clientSociety} kind="client" />
              {/* Société de facturation = ligne dans la carte cliente (créa), pas une carte dédiée. */}
              <FieldRow
                label={t('society.billingSociety', { ns: 'missionDashboard' })}
                value={
                  billingDistinct && societies?.clientBillingSociety ? (
                    <GhostButton onClick={() => setMgmtDrawer('clientBillingSociety')}>
                      {societies.clientBillingSociety.name}
                      {societies.clientBillingSociety.status ? ` · ${societies.clientBillingSociety.status}` : ''}
                    </GhostButton>
                  ) : societies?.clientSociety ? (
                    <GhostButton onClick={() => setMgmtDrawer('clientSociety')}>
                      {t('actions.attach', { ns: 'missionDashboard' })}
                    </GhostButton>
                  ) : (
                    '—'
                  )
                }
                last
              />
            </Card>
          </Box>
        )}

        {/* Bons de commande */}
        <SectionLabel>{t('sections.purchaseOrders', { ns: 'missionDashboard' })}</SectionLabel>
        <Box>
          {poLoading ? (
            <Skel height={220} />
          ) : (
            <Card icon={<ReceiptLongRoundedIcon sx={cardIconSx} />} iconTone="bleu">
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '11px 16px',
                  borderRadius: dsV2.radius.sm,
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: '16px',
                  width: '100%',
                  ...(dashboard.purchaseOrders.data?.effectivePurchaseOrderRequired
                    ? {
                        background: dsV2.color.vertDim,
                        color: dsV2.color.vert,
                        border: `1px solid ${dsV2.color.vertBrd}`,
                      }
                    : {
                        background: dsV2.color.surface2,
                        color: dsV2.color.t3,
                        border: `1px solid ${dsV2.color.border}`,
                      }),
                }}
              >
                {dashboard.purchaseOrders.data?.effectivePurchaseOrderRequired
                  ? t('purchaseOrders.required', { ns: 'missionDashboard' })
                  : t('purchaseOrders.notRequired', { ns: 'missionDashboard' })}
              </Box>
              {/* Toggles BDC requis (mission + client de facturation) */}
              <Box
                sx={{
                  border: `1px solid ${dsV2.color.border}`,
                  borderRadius: dsV2.radius.sm,
                  padding: '16px 18px',
                  marginBottom: '20px',
                  background: dsV2.color.surface,
                }}
              >
                <SwitchRow
                  label={t('purchaseOrders.switchMission', { ns: 'missionDashboard' })}
                  checked={poMission}
                  onChange={toggleMissionPo}
                />
                <SwitchRow
                  label={
                    billingSociety?.name
                      ? t('purchaseOrders.switchClientWithName', {
                          ns: 'missionDashboard',
                          name: billingSociety.name,
                        })
                      : t('purchaseOrders.switchClient', { ns: 'missionDashboard' })
                  }
                  checked={poClient}
                  onChange={toggleClientPo}
                />
                <Box
                  sx={{
                    fontSize: '11.5px',
                    color: dsV2.color.amberDark,
                    background: dsV2.color.amberDim,
                    border: `1px solid ${dsV2.color.amberBrd}`,
                    borderRadius: dsV2.radius.sm,
                    padding: '9px 12px',
                    lineHeight: 1.5,
                  }}
                >
                  {t('purchaseOrders.clientFlagNote', { ns: 'missionDashboard' })}
                </Box>
              </Box>
              {purchaseOrders.length > 0 ? (
                <Box sx={{ border: `1px solid ${dsV2.color.border}`, borderRadius: dsV2.radius.sm, overflow: 'auto' }}>
                  <Box
                    component="table"
                    sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 620 }}
                  >
                    <Box component="thead">
                      <Box component="tr">
                        {[
                          t('purchaseOrders.columns.number', { ns: 'missionDashboard' }),
                          t('purchaseOrders.columns.status', { ns: 'missionDashboard' }),
                          t('purchaseOrders.columns.totalHT', { ns: 'missionDashboard' }),
                          t('purchaseOrders.columns.invoicedHT', { ns: 'missionDashboard' }),
                          t('purchaseOrders.columns.remaining', { ns: 'missionDashboard' }),
                          t('purchaseOrders.columns.period', { ns: 'missionDashboard' }),
                        ].map((h, i) => (
                          <Box
                            key={h}
                            component="th"
                            sx={{
                              textAlign: i >= 2 && i <= 4 ? 'right' : 'left',
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              color: dsV2.color.t4,
                              padding: '11px 14px',
                              background: dsV2.color.surface2,
                              borderBottom: `1px solid ${dsV2.color.border}`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {h}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {purchaseOrders.map((po) => (
                        <Box component="tr" key={po.recordId}>
                          <Box
                            component="td"
                            sx={{
                              padding: '13px 14px',
                              borderBottom: `1px solid ${dsV2.color.borderLight}`,
                              color: dsV2.color.noir,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {po.orderNumber || '—'}
                          </Box>
                          <Box
                            component="td"
                            sx={{ padding: '13px 14px', borderBottom: `1px solid ${dsV2.color.borderLight}` }}
                          >
                            <ChipV2 label={po.status ?? '—'} tone="neutral" size="small" />
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              padding: '13px 14px',
                              borderBottom: `1px solid ${dsV2.color.borderLight}`,
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                              color: dsV2.color.t2,
                            }}
                          >
                            {fmtEuro(po.amountHT)}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              padding: '13px 14px',
                              borderBottom: `1px solid ${dsV2.color.borderLight}`,
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                              color: dsV2.color.t2,
                            }}
                          >
                            {fmtEuro(po.invoicedAmountHT)}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              padding: '13px 14px',
                              borderBottom: `1px solid ${dsV2.color.borderLight}`,
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                              color: dsV2.color.noir,
                              fontWeight: 600,
                            }}
                          >
                            {fmtEuro(computeRemainingAmount(po))}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              padding: '13px 14px',
                              borderBottom: `1px solid ${dsV2.color.borderLight}`,
                              fontVariantNumeric: 'tabular-nums',
                              color: dsV2.color.t3,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {po.startDate ? fmtDate(po.startDate as unknown as string) : '—'} →{' '}
                            {po.endDate ? fmtDate(po.endDate as unknown as string) : '—'}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', color: dsV2.color.t4, fontStyle: 'italic', padding: '20px' }}>
                  {t('purchaseOrders.empty', { ns: 'missionDashboard' })}
                </Box>
              )}
            </Card>
          )}
        </Box>

        {/* Paramètres contractuels */}
        <SectionLabel>{t('sections.contractSettings', { ns: 'missionDashboard' })}</SectionLabel>
        {headerLoading || pricingLoading ? (
          <Box>
            <Skel height={140} />
          </Box>
        ) : (
          <Box>
            <Card
              icon={<GavelRoundedIcon sx={cardIconSx} />}
              iconTone="neutral"
              title={t('cards.contractDetails', { ns: 'missionDashboard' })}
              action={
                <GhostButton onClick={() => openPopin('modify')}>
                  {t('actions.modify', { ns: 'missionDashboard' })}
                </GhostButton>
              }
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '0 40px' }}>
                <FieldRow
                  label={t('contract.type', { ns: 'missionDashboard' })}
                  value={
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                      {header?.contractType ? (
                        <ChipV2
                          label={t(`contractTypeLabels.${header.contractType}`, {
                            ns: 'missionDashboard',
                            defaultValue: header.contractType,
                          })}
                          tone={CONTRACT_TYPE_TONE[header.contractType] ?? 'info'}
                          size="small"
                        />
                      ) : (
                        '—'
                      )}
                      <ButtonV2
                        variant="link"
                        size="small"
                        onClick={() => setMgmtDrawer('clientSociety')}
                        ariaLabel="modifier-type-contrat"
                      >
                        {t('actions.modify', { ns: 'missionDashboard' })}
                      </ButtonV2>
                    </Box>
                  }
                />
                <FieldRow
                  label={t('contract.baseHours', { ns: 'missionDashboard' })}
                  value={
                    header?.hoursPerDays
                      ? t('contract.hoursUnit', { ns: 'missionDashboard', hours: header.hoursPerDays })
                      : undefined
                  }
                />
                <FieldRow
                  label={t('contract.tva', { ns: 'missionDashboard' })}
                  value={
                    currentPricing?.taxes != null
                      ? t('contract.tvaUnit', { ns: 'missionDashboard', value: currentPricing.taxes })
                      : undefined
                  }
                />
                <FieldRow
                  label={t('contract.uploadClientCra', { ns: 'missionDashboard' })}
                  value={
                    header?.enableCRAUpload
                      ? t('contract.craUploadAllowed', { ns: 'missionDashboard' })
                      : t('contract.craUploadDenied', { ns: 'missionDashboard' })
                  }
                />
                <FieldRow
                  label={t('contract.bloomerPaymentDelay', { ns: 'missionDashboard' })}
                  value={fmtDays(header?.bloomerPaydateDays)}
                  last
                />
                <FieldRow
                  label={t('contract.clientPaymentDelay', { ns: 'missionDashboard' })}
                  value={fmtDays(header?.clientPaydateDays)}
                  last
                />
              </Box>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WithErrorBoundary(MissionDashboardV2);
